import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Payment.css';

const Payment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [reservationId, setReservationId] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');

  useEffect(() => {
    // Get payment URL and reservation ID from query params
    const url = searchParams.get('url');
    const resId = searchParams.get('reservationId');
    const restName = searchParams.get('restaurantName');

    if (!url || !resId) {
      setError('Invalid payment session. Please try booking again.');
      setLoading(false);
      return;
    }

    setPaymentUrl(decodeURIComponent(url));
    setReservationId(resId);
    setRestaurantName(restName || 'Restaurant');
    setLoading(false);
  }, [searchParams]);

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel this payment? Your reservation will remain pending.')) {
      navigate('/my-reservations');
    }
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  // Handle message from Paymob iframe (if using postMessage)
  useEffect(() => {
    const handleMessage = (event) => {
      // Paymob might send postMessage on completion
      // Check for Paymob domain
      if (event.origin.includes('paymob.com')) {
        console.log('Paymob message:', event.data);
        // Redirect to success page
        if (event.data?.success !== undefined) {
          navigate(`/payment-success?reservationId=${reservationId}&success=${event.data.success}`);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [reservationId, navigate]);

  if (error) {
    return (
      <>
        <Navbar />
        <div className="payment-page-container">
          <div className="payment-error-card">
            <div className="error-icon">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>
            <h2>Payment Error</h2>
            <p>{error}</p>
            <button className="btn-back" onClick={() => navigate('/')}>
              <i className="bi bi-arrow-left me-2"></i>
              Back to Home
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="payment-page-container">
        <div className="payment-header">
          <h1>Complete Your Payment</h1>
          <p className="payment-subtitle">
            Secure payment for your reservation at <strong>{restaurantName}</strong>
          </p>
        </div>

        <div className="payment-content">
          {loading && (
            <div className="payment-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading payment form...</p>
            </div>
          )}

          <div className="payment-iframe-container">
            {paymentUrl && (
              <iframe
                src={paymentUrl}
                className="payment-iframe"
                title="Paymob Payment"
                onLoad={handleIframeLoad}
                allow="payment"
              />
            )}
          </div>

          <div className="payment-footer">
            <div className="payment-info">
              <i className="bi bi-shield-lock-fill me-2"></i>
              Your payment is secured by Paymob
            </div>
            <button className="btn-cancel" onClick={handleCancel}>
              <i className="bi bi-x-circle me-2"></i>
              Cancel Payment
            </button>
          </div>

          <div className="payment-help">
            <p>
              <i className="bi bi-info-circle me-2"></i>
              After completing payment, you will be redirected to a confirmation page.
              If you encounter any issues, please contact support.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Payment;
